import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import useSeo from "@/hooks/useSeo";
import { contentPrinciples, siteHighlights, siteMeta, testimonials } from "@/mocks/community";
import ContactForm from "./components/ContactForm";

export default function About() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const target = document.querySelector(location.hash);
      if (target) {
        window.setTimeout(() => target.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
      }
    }
  }, [location.hash]);

  const jsonLd = useMemo(() => {
    const origin = window.location.origin;
    return [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: `${siteMeta.name} · 关于我们`,
        description: siteMeta.intro,
        url: `${origin}/about`,
        mainEntity: {
          "@type": "Organization",
          name: siteMeta.name,
          email: siteMeta.email,
          foundingDate: siteMeta.foundedAt,
          description: siteMeta.intro,
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: origin },
          { "@type": "ListItem", position: 2, name: "关于我们", item: `${origin}/about` },
        ],
      },
    ];
  }, []);

  useSeo({
    title: "关于我们 | 爆料社区的品牌定位与内容原则",
    description:
      "了解爆料社区的品牌定位、发展理念与内容原则，查看用户口碑与联系方式。我们坚持来源可追溯、对立声音并存，让爆料社区的讨论更有秩序。",
    keywords: "爆料社区,关于我们,内容原则,品牌定位,用户口碑",
    path: "/about",
    type: "website",
    jsonLd,
  });

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-container px-4 pb-10 pt-24 md:px-6 md:pb-12 md:pt-28">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <div className="mt-4 max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-primary-600">
              <span className="h-px w-6 bg-primary-400" aria-hidden="true" />
              About {siteMeta.name}
            </span>
            <h1 className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
              关于爆料社区
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">{siteMeta.intro}</p>
          </div>
          <dl className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {siteHighlights.map((item) => (
              <div key={item.label} className="rounded-xl border border-background-200 bg-background-50 p-4">
                <dt className="text-xs text-foreground-500">{item.label}</dt>
                <dd className="mt-1.5 font-heading text-base font-bold text-foreground-950 md:text-lg">{item.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="mx-auto grid max-w-container gap-10 px-4 md:px-6 lg:grid-cols-2 lg:items-center">
          <div className="overflow-hidden rounded-2xl bg-background-100">
            <div className="aspect-[4/3] w-full">
              <img
                src="https://readdy.ai/api/search-image?query=warm%20editorial%20abstract%20illustration%20of%20people%20gathering%20around%20glowing%20conversation%20bubbles%2C%20cream%20paper%20texture%20with%20crimson%20and%20amber%20gradient%20shapes%2C%20soft%20studio%20lighting%2C%20minimal%20modern%20composition%2C%20high%20detail%2C%20no%20text&width=1200&height=900&seq=baoliao-about-brand&orientation=landscape"
                alt="爆料社区居民围绕话题讨论的抽象插画"
                title="关于爆料社区 - 品牌定位"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-3xl">
              一个专注「值得围观」的内容社区
            </h2>
            <div className="mt-5 space-y-4 text-sm leading-loose text-foreground-700 md:text-[15px]">
              <p>
                爆料社区成立于 {siteMeta.foundedAt} 年，最初的形态只是一个整理热点链接的小站。随着参与讨论的人越来越多，我们逐渐把重点放在两件事上：
                让热门话题更快被看见，让讨论更有秩序。
              </p>
              <p>
                今天，我们用今日活跃话题榜和板块热榜来组织内容，用高互动爆料帖来呈现讨论的热度。
                无论你只是想安静围观，还是想认真表达观点，都能在这里找到合适的入口。
              </p>
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="rounded-xl border border-background-200 bg-background-100 p-4">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
                  <i className="ri-focus-3-line text-primary-500" aria-hidden="true" />
                  网站定位
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  以热度信号为核心的话题聚合与讨论平台，而不是单纯的信息搬运站。
                </p>
              </div>
              <div className="rounded-xl border border-background-200 bg-background-100 p-4">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
                  <i className="ri-seedling-line text-secondary-500" aria-hidden="true" />
                  发展理念
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  慢一点核实，快一点澄清。热度可以带来关注，但不应该替代事实。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="principles" className="scroll-mt-24 bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-container px-4 md:px-6">
          <div className="max-w-2xl">
            <span className="text-xs font-medium uppercase tracking-widest text-primary-600">内容原则</span>
            <h2 className="mt-3 font-heading text-xl font-bold text-foreground-950 md:text-3xl">
              四条我们一直在坚持的事
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              这些原则决定了什么样的内容会被推荐上热榜，也决定了我们在争议发生时如何取舍。
            </p>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {contentPrinciples.map((item, index) => (
              <div key={item.title} className="flex gap-4 rounded-xl border border-background-200 bg-background-50 p-5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                  <i className={`${item.icon} text-xl`} aria-hidden="true" />
                </span>
                <div>
                  <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                    <span className="text-xs font-normal text-foreground-400">0{index + 1}</span>
                    {item.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="mx-auto max-w-container px-4 md:px-6">
          <div className="max-w-2xl">
            <span className="text-xs font-medium uppercase tracking-widest text-primary-600">用户口碑</span>
            <h2 className="mt-3 font-heading text-xl font-bold text-foreground-950 md:text-3xl">
              他们这样使用爆料社区
            </h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((item) => (
              <figure key={item.name} className="flex flex-col rounded-xl border border-background-200 bg-background-100 p-5">
                <i className="ri-double-quotes-l text-2xl text-accent-500" aria-hidden="true" />
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{item.content}</blockquote>
                <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span
                    className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold text-background-50 ${item.avatarClass}`}
                    aria-hidden="true"
                  >
                    {item.name.slice(0, 1)}
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-foreground-900">{item.name}</span>
                    <span className="block text-xs text-foreground-500">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section id="submit" className="scroll-mt-24 bg-background-100 py-12 md:py-16">
        <div className="mx-auto grid max-w-container gap-10 px-4 md:px-6 lg:grid-cols-[0.85fr_1.15fr]">
          <div>
            <span className="text-xs font-medium uppercase tracking-widest text-primary-600">联系方式</span>
            <h2 className="mt-3 font-heading text-xl font-bold text-foreground-950 md:text-3xl">
              有线索，或者想聊聊合作
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              投稿支持匿名，我们更在意信息是否完整、是否可核实。你也可以直接发邮件，我们会在工作时间内回复。
            </p>
            <dl className="mt-7 space-y-4">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-mail-line text-lg" aria-hidden="true" />
                </span>
                <div>
                  <dt className="text-xs text-foreground-500">联系邮箱</dt>
                  <dd>
                    <a
                      href={`mailto:${siteMeta.email}`}
                      className="text-sm font-medium text-foreground-900 transition-colors hover:text-primary-600"
                    >
                      {siteMeta.email}
                    </a>
                  </dd>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                  <i className="ri-time-line text-lg" aria-hidden="true" />
                </span>
                <div>
                  <dt className="text-xs text-foreground-500">工作时间</dt>
                  <dd className="text-sm font-medium text-foreground-900">{siteMeta.workingHours}</dd>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                  <i className="ri-shield-user-line text-lg" aria-hidden="true" />
                </span>
                <div>
                  <dt className="text-xs text-foreground-500">隐私说明</dt>
                  <dd className="text-sm font-medium text-foreground-900">投稿信息仅用于核实，不会公开</dd>
                </div>
              </div>
            </dl>
          </div>
          <ContactForm />
        </div>
      </section>
    </SiteLayout>
  );
}